import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, Users } from 'lucide-react';
import { Modal } from '@renderer/components/ui/Modal';
import { PinPad } from '@renderer/components/PinPad';
import { useSessionStore } from '@renderer/store/useSessionStore';
import type { MesaVista, UsuarioVista } from '@shared/types';

/**
 * Sprint 172 (D-1) — **Entregar mis mesas** (mesero) o **reasignar mesas** (caja / dueña).
 *
 * Tres pasos en un mismo aparato, como en el piso:
 *   1. Qué mesas (con "Todas").
 *   2. A quién (un botón por compañero).
 *   3. Los PIN: el de quien entrega y —entre meseros— el de quien recibe. La caja o la dueña sólo ponen
 *      el suyo: el mesero ya se fue y el que recibe está atendiendo.
 *
 * Los PIN se comprueban en el servidor (`entregaTurnoService.ts`); esta ventana sólo los junta. El botón
 * se bloquea **antes** de esperar la respuesta: un doble toque no hace dos entregas (lección del Sprint 159).
 */

export interface MesaEntregable {
  pedidoId: number;
  mesaNumero: number;
  folio: number;
  atiendeNombre?: string | null;
}

interface Props {
  abierto: boolean;
  modo: 'entregar' | 'reasignar';
  mesas: MesaEntregable[];
  onCerrar: () => void;
  onListo: (mensaje: string) => void;
}

type Paso = 'mesas' | 'quien' | 'pin-propio' | 'pin-recibe';

export function EntregarTurnoModal({ abierto, modo, mesas, onCerrar, onListo }: Props) {
  const sesion = useSessionStore((s) => s.sesion);
  const [paso, setPaso] = useState<Paso>('mesas');
  const [elegidas, setElegidas] = useState<Set<number>>(new Set());
  const [companeros, setCompaneros] = useState<UsuarioVista[]>([]);
  const [recibe, setRecibe] = useState<UsuarioVista | null>(null);
  const [pinPropio, setPinPropio] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reset, setReset] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const enviandoRef = useRef(false);

  useEffect(() => {
    if (!abierto) return;
    setPaso('mesas');
    setElegidas(new Set(mesas.length === 1 ? [mesas[0].pedidoId] : []));
    setRecibe(null);
    setPinPropio('');
    setError(null);
    void window.api.auth.listarUsuarios().then((r) => {
      if (!r.ok) return;
      // Entre meseros se entrega a un compañero; la caja o la dueña pueden pasarlas a cualquier mesero.
      setCompaneros(r.data.filter((u) => u.rol === 'mesero' && u.id !== sesion?.usuarioId));
    });
  }, [abierto]); // eslint-disable-line react-hooks/exhaustive-deps

  const ordenadas = useMemo(() => [...mesas].sort((a, b) => a.mesaNumero - b.mesaNumero), [mesas]);

  if (!abierto || !sesion) return null;

  const todas = elegidas.size === mesas.length && mesas.length > 0;
  const alternar = (id: number) =>
    setElegidas((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  const enviar = async (pinRecibe?: string, pin: string = pinPropio) => {
    if (enviandoRef.current || !recibe) return;
    enviandoRef.current = true;
    setEnviando(true);
    setError(null);
    const r = await window.api.pedidos.entregarTurno({
      pedidoIds: [...elegidas],
      aUsuarioId: recibe.id,
      pinPropio: pin,
      ...(pinRecibe ? { pinRecibe } : {}),
    });
    enviandoRef.current = false;
    setEnviando(false);
    if (r.ok) {
      const n = r.data.cantidad;
      onListo(`Listo: ${r.data.aNombre} ya atiende ${n === 1 ? 'la mesa' : `las ${n} mesas`}.`);
      return;
    }
    setError(r.error ?? 'No se pudo entregar. Intenta de nuevo.');
    // Si el PIN equivocado fue el propio, se vuelve a pedir el propio.
    if (/Tu PIN/.test(r.error ?? '')) {
      setPinPropio('');
      setPaso('pin-propio');
    }
    setReset((x) => x + 1);
  };

  // Sprint 172 — "Pasar mesas" es lo que se dice en el piso ("reasignar" no se dice en una cocina).
  const titulo = modo === 'entregar' ? 'Entregar mis mesas' : 'Pasar mesas a otro mesero';
  const subtitulo =
    paso === 'mesas'
      ? 'Paso 1 de 3 · ¿Qué mesas?'
      : paso === 'quien'
        ? 'Paso 2 de 3 · ¿A quién?'
        : 'Paso 3 de 3 · Confirmar con PIN';

  const volver = () => {
    setError(null);
    if (paso === 'quien') setPaso('mesas');
    else if (paso === 'pin-propio') setPaso('quien');
    else if (paso === 'pin-recibe') {
      setPinPropio('');
      setPaso('pin-propio');
    }
  };

  const pie = (
    <div className="flex items-center justify-between gap-3">
      {paso === 'mesas' ? (
        <button type="button" onClick={onCerrar} className="min-h-[44px] rounded-lg px-4 text-base font-medium text-slate-700 hover:bg-slate-100">
          Cancelar
        </button>
      ) : (
        <button type="button" onClick={volver} disabled={enviando} className="flex min-h-[44px] items-center gap-2 rounded-lg px-4 text-base font-medium text-slate-700 hover:bg-slate-100">
          <ArrowLeft className="h-5 w-5" /> Atrás
        </button>
      )}
      {paso === 'mesas' && (
        <div className="flex items-center gap-3">
          {/* Sprint 172 (ux-master #7) — Un botón apagado dice por qué. */}
          {elegidas.size === 0 && mesas.length > 0 && <span className="text-sm text-slate-700">Elige al menos una mesa</span>}
          <button
            type="button"
            disabled={elegidas.size === 0}
            onClick={() => setPaso('quien')}
            className="min-h-[44px] rounded-lg bg-primary px-5 text-base font-semibold text-white disabled:bg-slate-300 disabled:text-slate-700"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Modal abierto={abierto} titulo={titulo} subtitulo={subtitulo} pie={pie} onCerrar={enviando ? undefined : onCerrar} dataTour="entregar-turno">
      {paso === 'mesas' && (
        <div className="space-y-3">
          {mesas.length === 0 ? (
            <p className="text-base text-slate-700">No hay mesas abiertas para entregar.</p>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setElegidas(todas ? new Set() : new Set(mesas.map((m) => m.pedidoId)))}
                className={`flex min-h-[48px] w-full items-center justify-between rounded-xl border-2 px-4 text-base font-semibold ${
                  todas ? 'border-primary bg-primary/10 text-slate-900' : 'border-slate-300 text-slate-800'
                }`}
              >
                Todas ({mesas.length})
                {todas && <Check className="h-5 w-5 text-primary" />}
              </button>
              <div className="grid grid-cols-2 gap-2">
                {ordenadas.map((m) => {
                  const marcada = elegidas.has(m.pedidoId);
                  return (
                    <button
                      key={m.pedidoId}
                      type="button"
                      onClick={() => alternar(m.pedidoId)}
                      aria-pressed={marcada}
                      className={`flex min-h-[56px] flex-col items-start justify-center rounded-xl border-2 px-3 text-left ${
                        marcada ? 'border-primary bg-primary/10' : 'border-slate-300'
                      }`}
                    >
                      <span className="flex w-full items-center justify-between text-lg font-bold text-slate-900">
                        Mesa {m.mesaNumero}
                        {/* Sprint 172 (ux-master #1) — Marcada se ve con palomita, no sólo con color. */}
                        {marcada && <Check className="h-5 w-5 text-primary" aria-hidden />}
                      </span>
                      <span className="text-sm text-slate-700">
                        #{String(m.folio).padStart(5, '0')}
                        {modo === 'reasignar' && m.atiendeNombre ? ` · ${m.atiendeNombre}` : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {paso === 'quien' && (
        <div className="space-y-2">
          {companeros.length === 0 ? (
            <p className="text-base text-slate-700">
              No hay otro mesero dado de alta con acceso. Pídele a la dueña que dé de alta a tu compañero en Usuarios.
            </p>
          ) : (
            companeros.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => {
                  setRecibe(u);
                  setPaso('pin-propio');
                  setReset((x) => x + 1);
                }}
                className="flex min-h-[52px] w-full items-center gap-3 rounded-xl border-2 border-slate-300 px-4 text-left text-lg font-semibold text-slate-900 hover:border-primary"
              >
                <Users className="h-5 w-5 text-slate-600" /> {u.nombre}
              </button>
            ))
          )}
        </div>
      )}

      {paso === 'pin-propio' && recibe && (
        <div className="space-y-3">
          <p className="text-center text-base text-slate-800">
            {elegidas.size === 1 ? '1 mesa' : `${elegidas.size} mesas`} para <strong>{recibe.nombre}</strong>.
          </p>
          <PinPad
            label={`${sesion.nombre}, pon tu PIN`}
            resetSignal={reset}
            error={error ?? undefined}
            onComplete={(pin) => {
              setPinPropio(pin);
              if (modo === 'entregar') {
                setError(null);
                setPaso('pin-recibe');
                setReset((x) => x + 1);
              } else {
                void enviar(undefined, pin);
              }
            }}
          />
        </div>
      )}

      {paso === 'pin-recibe' && recibe && (
        <div className="space-y-3">
          <p className="text-center text-base text-slate-800">Ahora pásale el aparato a <strong>{recibe.nombre}</strong>.</p>
          <PinPad
            label={`${recibe.nombre}, pon tu PIN para recibirlas`}
            resetSignal={reset}
            error={error ?? undefined}
            onComplete={(pin) => void enviar(pin)}
          />
        </div>
      )}

      {enviando && <p className="mt-3 text-center text-base text-slate-700">Entregando…</p>}
    </Modal>
  );
}

/** Las mesas entregables a partir del mapa de mesas y los pedidos vivos que ve el actor. */
export function mesasEntregables(
  mesas: MesaVista[],
  pedidos: Array<{ id: number; numeroPedido: number; tipo?: string | null; mesaId?: number | null; mesaIds?: number[] | null; pedidoPadreId?: number | null; meseroAtiendeId?: number | null; usuarioId?: number | null }>,
  soloDe?: number,
): MesaEntregable[] {
  const numero = new Map(mesas.map((m) => [m.id, m.numero]));
  const nombreAtiende = new Map(mesas.filter((m) => m.pedidoActivoId).map((m) => [m.pedidoActivoId as number, m.meseroAtiendeNombre ?? null]));
  return pedidos
    .filter((p) => p.tipo === 'comer_aqui' && p.mesaId && !p.pedidoPadreId)
    .filter((p) => soloDe === undefined || (p.meseroAtiendeId ?? p.usuarioId) === soloDe)
    .map((p) => ({
      pedidoId: p.id,
      mesaNumero: numero.get(p.mesaId as number) ?? 0,
      folio: p.numeroPedido,
      atiendeNombre: nombreAtiende.get(p.id) ?? null,
    }));
}
